import { createSlice, PayloadAction } from "@reduxjs/toolkit"

/**
 * WebSocket connection state
 */
export interface WebSocketState {
  isConnected: boolean
  isReconnecting: boolean
  reconnectAttempt: number
  permanentlyDisconnected: boolean
  error: string | null
  userId?: string
  clientId?: string
  socketId?: string
}

/**
 * Initial WebSocket state
 */
const initialState: WebSocketState = {
  isConnected: false,
  isReconnecting: false,
  reconnectAttempt: 0,
  permanentlyDisconnected: false,
  error: null,
  userId: undefined,
  clientId: undefined,
  socketId: undefined
}

/**
 * WebSocket slice
 */
export const websocketSlice = createSlice({
  name: "websocket",
  initialState,
  reducers: {
    /**
     * Set WebSocket connection status
     */
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload
      if (!action.payload) {
        // Clear connection details on disconnect
        state.userId = undefined
        state.clientId = undefined
        state.socketId = undefined
        state.error = null
      } else {
        // Clear reconnection state on successful connection
        state.isReconnecting = false
        state.reconnectAttempt = 0
        state.permanentlyDisconnected = false
      }
    },

    /**
     * Set WebSocket connection details
     */
    setConnectionDetails: (
      state,
      action: PayloadAction<{
        userId?: string
        clientId?: string
        socketId?: string
        isReconnecting?: boolean
        reconnectAttempt?: number
        permanentlyDisconnected?: boolean
      }>
    ) => {
      if (action.payload.userId !== undefined) state.userId = action.payload.userId
      if (action.payload.clientId !== undefined) state.clientId = action.payload.clientId
      if (action.payload.socketId !== undefined) state.socketId = action.payload.socketId
      if (action.payload.isReconnecting !== undefined)
        state.isReconnecting = action.payload.isReconnecting
      if (action.payload.reconnectAttempt !== undefined)
        state.reconnectAttempt = action.payload.reconnectAttempt
      if (action.payload.permanentlyDisconnected !== undefined)
        state.permanentlyDisconnected = action.payload.permanentlyDisconnected
    },

    /**
     * Set WebSocket error
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },

    /**
     * Clear WebSocket error
     */
    clearError: (state) => {
      state.error = null
    }
  }
})

export const { setConnected, setConnectionDetails, setError, clearError } = websocketSlice.actions

export const websocketReducer = websocketSlice.reducer
